import { Injectable } from '@angular/core';

export interface ImageSize {
  width: number;
  height: number;
}

export interface ContentstackImageModel {
  hub: string;
  assetId: string;
  version?: string;
  fileName: string;
}

@Injectable()
export class ContentStackImageService {
  static readonly BASE_URL = 'https://azure-na-images.contentstack.com/v3/assets';

  /**
   * Generates a plain image URL with size and optional optimization query params.
   * Use this when binding to an HTML `src` attribute.
   */
  public generateImageUrl(asset: ContentstackImageModel | string, size: ImageSize, optimize = true): string {
    return this._buildUrl(asset, size, optimize);
  }

  /**
   * Generates an image URL wrapped in double quotes, ready to be embedded inside a CSS `url()` expression.
   * Use this when the URL will be used as `'url(' + imageUrl + ')'` in a template.
   */
  public generateCssImageUrl(asset: ContentstackImageModel | string, size: ImageSize, optimize = true): string {
    const url = this._buildUrl(asset, size, optimize);
    return url ? `"${url}"` : '';
  }

  private _buildUrl(asset: ContentstackImageModel | string, size: ImageSize, optimize: boolean): string {
    const baseUrl = typeof asset === 'string' ? asset : this._buildFullUrl(asset);

    if (!baseUrl) {
      return '';
    }

    if (size.width <= 0) {
      throw new Error('[ContentStackImageComponent] Invalid input: width value must be positive');
    }
    if (size.height <= 0) {
      throw new Error('[ContentStackImageComponent] Invalid input: height value must be positive');
    }

    const url = new URL(baseUrl);

    url.searchParams.set('width', size.width.toString());
    url.searchParams.set('height', size.height.toString());

    if (optimize) {
      url.searchParams.set('auto', 'webp');
    }

    return url.toString();
  }

  private _buildFullUrl(model: ContentstackImageModel): string {
    const parts = [ContentStackImageService.BASE_URL, model.hub, model.assetId];

    if (model.version) {
      parts.push(model.version);
    }

    parts.push(model.fileName);
    return parts.join('/');
  }
}
